import React from 'react';
import { Search, Share2, Info, Tag } from 'lucide-react';

export default function SeoPreview({ form = {} }) {
  const pageName = form.page_name || 'Home';
  const title = form.seo_title || 'ZOBOX | Refurbished Technology Ecosystem';
  const description = form.seo_description || 'Buy quality refurbished smartphones and laptops with warranty and doorstep delivery across India.';
  const canonical = form.canonical_url || 'https://zobox.in/';
  const ogImage = form.og_image || '';

  const keywordsList = typeof form.seo_keywords === 'string'
    ? form.seo_keywords.split(',').map((k) => k.trim()).filter(Boolean)
    : [];

  return (
    <div className="bg-slate-50 min-h-full flex flex-col font-sans text-gray-900">
      {/* Top Bar */}
      <div className="bg-white border-b px-4 py-2.5 flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-500">Target Page: <strong>{pageName}</strong></span>
        <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold uppercase text-[10px]">
          SEO &amp; Social Card
        </span>
      </div>

      <div className="p-6 md:p-8 space-y-8 flex-1 max-w-2xl mx-auto w-full">
        {/* Search Engine Result Card */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider">
            <Search className="w-3.5 h-3.5" />
            <span>Search Engine Result Preview (Google)</span>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-slate-100 border flex items-center justify-center text-[10px] font-bold text-[#2B3380]">
                Z
              </div>
              <div className="text-[11px] text-slate-600 truncate">
                {canonical}
              </div>
            </div>

            <h3 className="text-base font-semibold text-blue-800 hover:underline cursor-pointer truncate">
              {title}
            </h3>

            <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">
              {description}
            </p>
          </div>
        </div>

        {/* Social Media Share Preview (OG Card) */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider">
            <Share2 className="w-3.5 h-3.5" />
            <span>Social Share Card Preview (WhatsApp / Facebook / LinkedIn)</span>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden max-w-lg">
            <div className="h-48 bg-slate-100 flex items-center justify-center overflow-hidden relative">
              {ogImage ? (
                <img
                  src={ogImage}
                  alt="OG Share"
                  className="w-full h-full object-cover"
                  onError={(e) => (e.target.style.display = 'none')}
                />
              ) : (
                <div className="text-slate-400 text-xs text-center p-4">
                  <p>No Social Share Image Uploaded</p>
                  <span className="text-[10px]">Add an OG image for rich social media cards</span>
                </div>
              )}
            </div>

            <div className="p-4 space-y-1 bg-slate-50 border-t">
              <span className="text-[10px] uppercase font-bold text-slate-400">
                {new URL(canonical || 'https://zobox.in').hostname}
              </span>
              <h4 className="font-bold text-slate-900 text-sm truncate">{title}</h4>
              <p className="text-xs text-slate-600 line-clamp-2">{description}</p>
            </div>
          </div>
        </div>

        {/* Keywords */}
        {keywordsList.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider">
              <Tag className="w-3.5 h-3.5" />
              <span>Target Meta Keywords ({keywordsList.length})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {keywordsList.map((k, i) => (
                <span key={i} className="px-2.5 py-1 rounded-md bg-white border text-xs text-slate-700 font-medium">
                  {k}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer Alert */}
        <div className="p-3.5 bg-blue-50/70 border border-blue-200 rounded-xl text-xs text-blue-800 flex items-start gap-2.5">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
          <p>
            <strong>Illustrative Preview:</strong> This preview shows how search engines and social platforms typically format metadata. Actual appearance is dynamically adjusted by individual platforms.
          </p>
        </div>
      </div>
    </div>
  );
}
